/**
 * Live Gift Sound synthesis — no external audio files, no CDN. Everything
 * is generated on the fly with the Web Audio API. Keeps the app tiny and
 * bypasses autoplay/CORS/MIME headaches that come with real .mp3 assets.
 *
 * The "coin cascade" is 3 layers stacked:
 *   1. `whoosh` — a short filtered-noise burst at t=0 that says "something
 *      big just happened" before the metallic pings start.
 *   2. `pings`  — a shower of quick oscillator-based coin sounds spread
 *      across the tier's duration. Each ping combines a bright triangle
 *      wave (~1200-2200 Hz for the metallic ring) + a very short noise
 *      burst (for the "clink" attack).
 *   3. `fanfare` — a soft brass-like chord at the start of Crown/Galaxy
 *      tiers so the biggest gifts feel ceremonial.
 *
 * User preference persisted at `localStorage.neosync:live:sfx` (default
 * "on"). Volume is capped fairly low so an unmuted browser doesn't
 * shock the viewer.
 */

const SFX_KEY = "neosync:live:sfx";
const MASTER_VOLUME = 0.35;   // 0..1, deliberately conservative

// Tier presets — matches the visual tiers in LiveGiftRain.jsx.
const TIER_SFX = {
  rocket: { pings: 18, spread: 3200, fanfare: false, pingPitchRange: [1200, 2200] },
  crown:  { pings: 32, spread: 4500, fanfare: true,  pingPitchRange: [900,  2400] },
  galaxy: { pings: 60, spread: 6500, fanfare: true,  pingPitchRange: [800,  2800] },
};

function tierForCoins(coins) {
  if (coins >= 5000) return "galaxy";
  if (coins >= 1000) return "crown";
  if (coins >= 500)  return "rocket";
  return null;
}

// Shared AudioContext (browsers cap the number you can open).
let _ctx = null;
function ctx() {
  if (_ctx && _ctx.state !== "closed") return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
}

// Prime the context — some browsers won't play sounds until the context
// has been "resumed" from a user gesture. Called on any hover / click of
// the gift catalog so by the time a rain fires the context is unlocked.
export function primeGiftSfx() {
  const c = ctx();
  if (c && c.state === "suspended") {
    c.resume().catch(() => {});
  }
}

export function isGiftSfxEnabled() {
  try { return localStorage.getItem(SFX_KEY) !== "0"; }
  catch { return true; }
}

export function setGiftSfxEnabled(on) {
  try { localStorage.setItem(SFX_KEY, on ? "1" : "0"); } catch { /* private mode */ }
}

function playPing(c, master, atMs, freq) {
  const t = c.currentTime + atMs / 1000;
  // Bright metallic ring — triangle oscillator with a slight detune.
  const osc = c.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(freq, t);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.85, t + 0.18);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.5, t + 0.005);      // fast attack
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22); // decay
  osc.connect(gain).connect(master);
  osc.start(t);
  osc.stop(t + 0.24);

  // A very short noise chip for the "clink" transient.
  const buf = c.createBuffer(1, c.sampleRate * 0.03, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
  const src = c.createBufferSource();
  src.buffer = buf;
  const hp = c.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 2500;
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.15, t);
  ng.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
  src.connect(hp).connect(ng).connect(master);
  src.start(t);
  src.stop(t + 0.05);
}

function playWhoosh(c, master) {
  const t = c.currentTime;
  const buf = c.createBuffer(1, c.sampleRate * 0.4, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(400, t);
  bp.frequency.exponentialRampToValueAtTime(2000, t + 0.35);
  bp.Q.value = 1.5;
  const g = c.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.25, t + 0.05);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
  src.connect(bp).connect(g).connect(master);
  src.start(t);
  src.stop(t + 0.4);
}

function playFanfare(c, master) {
  const t = c.currentTime;
  // Simple triadic chord (C-E-G) — feels ceremonial without being cheesy.
  [523.25, 659.25, 783.99].forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = freq;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t + i * 0.03);
    g.gain.linearRampToValueAtTime(0.06, t + i * 0.03 + 0.08);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
    // Low-pass for warmth
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1400;
    osc.connect(lp).connect(g).connect(master);
    osc.start(t + i * 0.03);
    osc.stop(t + 1.0);
  });
}

/**
 * Public entry — play the tier-appropriate sound cascade for a live
 * gift. Silently no-ops if the user has muted, the browser doesn't
 * support Web Audio, or the audio context is still suspended.
 */
export function playGiftRainSfx(coins) {
  if (!isGiftSfxEnabled()) return;
  const tier = tierForCoins(coins);
  if (!tier) return;
  const c = ctx();
  if (!c) return;
  // Auto-resume if suspended (Safari especially likes to keep suspending).
  if (c.state === "suspended") c.resume().catch(() => {});
  const spec = TIER_SFX[tier];

  const master = c.createGain();
  master.gain.value = MASTER_VOLUME;
  master.connect(c.destination);

  // Whoosh at the start.
  playWhoosh(c, master);

  // Fanfare for the two biggest tiers.
  if (spec.fanfare) playFanfare(c, master);

  // Ping cascade — random pitches within the tier's range, exponential
  // distribution of times so the density feels natural (dense at first,
  // trailing off).
  const [fLo, fHi] = spec.pingPitchRange;
  for (let i = 0; i < spec.pings; i++) {
    const bias = Math.pow(i / spec.pings, 1.2); // front-loaded
    const atMs = 80 + bias * spec.spread + Math.random() * 80;
    const freq = fLo + Math.random() * (fHi - fLo);
    playPing(c, master, atMs, freq);
  }
}
